import React, { useEffect, useState } from "react";
import AdminLayout from "../../components/layout/AdminLayout";
import { api } from "../../lib/api";
import { Dices, TrendingUp, TrendingDown, User, Rocket, Flame, Coins } from "lucide-react";
import { Input } from "../../components/ui/input";

const GAME_META = {
  aviator:      { label: "AVIATOR",      icon: Rocket,  hue: "from-fuchsia-600 to-rose-600" },
  dragon_tiger: { label: "DRAGON TIGER", icon: Flame,   hue: "from-rose-700 to-amber-500" },
  coin_flip:    { label: "COIN FLIP",    icon: Coins,   hue: "from-amber-600 to-yellow-500" },
};

function statusMeta(b) {
  const s = (b.status || "").toLowerCase();
  if (s === "won") return { text: `+₹${b.payout ?? 0}`, cls: "text-emerald-300 bg-emerald-500/10 border-emerald-500/30" };
  if (s === "lost") return { text: `-₹${b.amount ?? 0}`, cls: "text-rose-300 bg-rose-500/10 border-rose-500/30" };
  return { text: "Pending", cls: "text-amber-300 bg-amber-500/10 border-amber-500/30" };
}

export default function AdminCasino() {
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(true);
  const [filter, setFilter] = useState("");

  const load = () => {
    setLoading(true);
    api.get("/admin/casino-history?limit=300")
      .then(({ data }) => setRows(Array.isArray(data) ? data : []))
      .finally(() => setLoading(false));
  };
  useEffect(load, []);

  const filtered = rows.filter((r) => {
    if (!filter.trim()) return true;
    const q = filter.trim().toLowerCase();
    return (
      String(r.user_mobile || "").includes(q) ||
      String(r.user_name || "").toLowerCase().includes(q) ||
      String(r.game_type || r.source || "").toLowerCase().includes(q)
    );
  });

  const totals = filtered.reduce(
    (a, b) => {
      const st = (b.status || "").toLowerCase();
      a.stake += b.amount || 0;
      if (st === "won") a.payout += b.payout || 0;
      if (st === "lost") a.loss += b.amount || 0;
      return a;
    },
    { stake: 0, payout: 0, loss: 0 }
  );

  return (
    <AdminLayout>
      <div className="mb-4 flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3">
        <div>
          <h1 className="text-xl md:text-2xl font-black text-slate-900 flex items-center gap-2">
            <Dices className="w-6 h-6 text-[#FF7A00]" /> Casino History
          </h1>
          <p className="text-xs text-slate-500 mt-0.5">Aviator · Dragon Tiger · Coin Flip — sabhi live-casino bets</p>
        </div>
        <Input
          data-testid="casino-search"
          placeholder="🔍 Search mobile / name / game…"
          value={filter}
          onChange={(e) => setFilter(e.target.value)}
          className="max-w-sm"
        />
      </div>

      {/* Totals strip (casino-style dark) */}
      <div className="grid grid-cols-3 gap-2 md:gap-3 mb-4">
        <div className="rounded-xl p-3 md:p-4 bg-gradient-to-br from-slate-900 to-slate-800 border border-slate-700 text-white">
          <div className="text-[10px] uppercase tracking-widest text-slate-400">Stake</div>
          <div className="text-lg md:text-xl font-black text-amber-300">₹{totals.stake.toLocaleString()}</div>
        </div>
        <div className="rounded-xl p-3 md:p-4 bg-gradient-to-br from-emerald-900 to-emerald-800 border border-emerald-700 text-white">
          <div className="text-[10px] uppercase tracking-widest text-emerald-300">Payout</div>
          <div className="text-lg md:text-xl font-black text-emerald-200">₹{totals.payout.toLocaleString()}</div>
        </div>
        <div className="rounded-xl p-3 md:p-4 bg-gradient-to-br from-rose-900 to-rose-800 border border-rose-700 text-white">
          <div className="text-[10px] uppercase tracking-widest text-rose-300">Loss</div>
          <div className="text-lg md:text-xl font-black text-rose-200">₹{totals.loss.toLocaleString()}</div>
        </div>
      </div>

      <CasinoRows rows={filtered} loading={loading} />
    </AdminLayout>
  );
}

export function CasinoRows({ rows, loading, hideUser = false }) {
  if (loading) return <div className="text-center py-12 text-slate-400 text-sm">Loading…</div>;
  if (!rows.length) return <div className="text-center py-12 text-slate-400 text-sm">Koi casino bet nahi mila.</div>;
  return (
    <div className="space-y-2">
      {rows.map((b, i) => {
        const meta = GAME_META[b.game_type] || { label: (b.game_type || "GAME").toUpperCase(), icon: Dices, hue: "from-slate-700 to-slate-900" };
        const st = statusMeta(b);
        const Icon = meta.icon;
        return (
          <div
            key={b.id || i}
            data-testid={`casino-row-${i}`}
            className={`relative flex items-center gap-3 p-3 md:p-4 rounded-xl border border-slate-700 bg-gradient-to-r ${meta.hue} text-white shadow-lg overflow-hidden`}
          >
            <div className="absolute inset-0 bg-black/30 pointer-events-none" />
            <div className="relative flex-shrink-0 w-11 h-11 md:w-12 md:h-12 rounded-lg bg-white/15 flex items-center justify-center backdrop-blur-sm">
              <Icon className="w-5 h-5 md:w-6 md:h-6" />
            </div>
            <div className="relative flex-1 min-w-0">
              <div className="flex items-center gap-2 flex-wrap">
                <span className="text-[10px] md:text-[11px] font-black tracking-wider uppercase bg-white/20 px-2 py-0.5 rounded">{meta.label}</span>
                <span className="text-[10px] text-white/70">#{(b.round_number || b.round_id || "").toString().slice(0, 8)}</span>
              </div>
              <div className="text-sm font-medium mt-0.5 truncate">
                Pick: <span className="font-black">{b.pick || b.choice || b.multiplier || b.side || "—"}</span>
                {b.cashout_multiplier ? <span className="text-amber-300 ml-1">@ {Number(b.cashout_multiplier).toFixed(2)}x</span> : null}
              </div>
              {!hideUser && (b.user_mobile || b.user_name) && (
                <div className="text-[11px] text-white/75 flex items-center gap-1 mt-0.5 truncate">
                  <User className="w-3 h-3" /> {b.user_name || "—"} · {b.user_mobile || ""}
                </div>
              )}
              <div className="text-[10px] text-white/60 mt-0.5">{new Date(b.created_at).toLocaleString("en-IN", { hour12: true })}</div>
            </div>
            <div className="relative text-right flex flex-col items-end gap-1">
              <div className="text-xs text-white/80">Stake: ₹{b.amount || 0}</div>
              <div className={`text-xs md:text-sm font-black px-2 py-1 rounded-md border ${st.cls} flex items-center gap-1`}>
                {(b.status || "").toLowerCase() === "won" ? <TrendingUp className="w-3 h-3" /> : (b.status || "").toLowerCase() === "lost" ? <TrendingDown className="w-3 h-3" /> : null}
                {st.text}
              </div>
            </div>
          </div>
        );
      })}
    </div>
  );
}
